import { useParams, Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Clock, User, ChevronRight } from "lucide-react";
import { articles, images } from "@/mock/mock";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <section className="pt-44 pb-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h1 className="font-display text-3xl font-bold text-white">Article not found</h1>
          <Button asChild className="mt-6 bg-brand-orange hover:bg-brand-orangeDark text-white rounded-full">
            <Link to="/insights">Back to Insights</Link>
          </Button>
        </div>
      </section>
    );
  }

  return (
    <article>
      <section className="relative pt-40 pb-14 lg:pt-44">
        <div className="absolute inset-0">
          <img src={images[article.image]} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/70 to-brand-navy" />
        </div>
        <div className="relative mx-auto max-w-3xl px-6">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Link to="/" className="hover:text-brand-lime transition-colors">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/insights" className="hover:text-brand-lime transition-colors">Insights</Link>
          </div>
          <span className="mt-5 inline-block rounded-full bg-brand-lime/15 px-3 py-1 text-xs font-semibold text-brand-lime">{article.category}</span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl font-bold leading-tight text-white">{article.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-400">
            <span className="flex items-center gap-1.5"><User className="h-4 w-4 text-brand-lime" /> {article.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-brand-lime" /> {article.date}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-brand-lime" /> {article.readTime}</span>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-3xl px-6">
          <div className="overflow-hidden rounded-2xl border border-white/10 mb-10">
            <img src={images[article.image]} alt={article.title} className="w-full h-[360px] object-cover" />
          </div>
          <div className="space-y-6">
            {article.body.map((p, i) => (
              <p key={i} className="text-lg leading-relaxed text-slate-300">{p}</p>
            ))}
          </div>
          <div className="mt-10 pt-8 border-t border-white/10">
            <Button asChild variant="outline" className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white">
              <Link to="/insights"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Insights</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-white/10 bg-brand-navy2/40">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-display text-2xl font-bold text-white mb-8">More insights</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 80}>
                <Link to={`/insights/${a.slug}`} className="group block h-full rounded-2xl border border-white/10 bg-brand-navy2/60 overflow-hidden card-hover">
                  <div className="relative h-40 overflow-hidden">
                    <img src={images[a.image]} alt={a.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-semibold uppercase tracking-wider text-brand-lime">{a.category}</span>
                    <h3 className="mt-2 font-display text-lg font-semibold text-white group-hover:text-brand-lime transition-colors">{a.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
